<script setup lang="ts">
  import {
    computed,
    inject,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
    watchEffect,
    WritableComputedRef
  } from 'vue'
  import { pendingRefocusKey } from '@/shared/models/pendingRefocus'
  import {
    Form,
    Input,
    InputNumber,
    Select,
    SelectOption,
    Checkbox,
    Button,
    AutoComplete
  } from 'ant-design-vue'
  import { AnalysisConfig } from '../models/AnalysisConfig'
  import { AnalysisConfigSchema } from '../models/AnalysisConfigSchema'
  import { AnalysisConfigFieldType } from '../models/AnalysisConfigFieldType'
  import { debounceTime, groupBy, map, mergeMap, Subject } from 'rxjs'
  import { parseSmartValue, replaceLast, DEFAULT_INPUT_DEBOUNCE_MS } from '@/utils'
  import { AnalysisConfigService } from '../services/AnalysisConfigService'
  import { MinusSquare, PlusSquare } from 'lucide-vue-next'
  import JsonAnalysisConfigConfiguration from './JsonAnalysisConfigConfiguration.vue'
  import { AnalysisConfigJsonConfigurationService } from '../services/AnalysisConfigJsonConfigurationService'
  import ArrayValuesAnalysisConfigConfiguration from './ArrayValuesAnalysisConfigConfiguration.vue'
  import SelectOrCreateAnalysisConfigConfiguration from './SelectOrCreateAnalysisConfigConfiguration.vue'
  import {
    AppstoreOutlined,
    BorderOutlined,
    CompressOutlined,
    ExpandOutlined
  } from '@ant-design/icons-vue'
  import ColumnMultiSelectOrCreateAnalysisConfigConfiguration from './ColumnMultiSelectOrCreateAnalysisConfigConfiguration.vue'
  import { AnalysisConfigField } from '../models/AnalysisConfigField'
  import AnalysisConfigHelpPopover from '../help/components/AnalysisConfigHelpPopover.vue'
  import RegexAnalysisConfigConfiguration from './RegexAnalysisConfigConfiguration.vue'
  import { AnalysisConfigSchemaUiFieldGroup } from '../models/AnalysisConfigSchemaUi'
  import ColorAnalysisConfigConfiguration from './ColorAnalysisConfigConfiguration.vue'
  import StringTemplateBuilder from '@/shared/StringTemplateBuilder/components/StringTemplateBuilder.vue'
  import ArrayAnalysisConfigBuilder from './ArrayAnalysisConfigBuilder.vue'
  import ObjectAnalysisConfigBuilder from './ObjectAnalysisConfigBuilder.vue'
  import { AnalysisConfigOption } from '../models/AnalysisConfigOption'
  import SelectXorTextAnalysisConfigConfiguration from './SelectXorTextAnalysisConfigConfiguration.vue'
  import SelectXorSelectAnalysisConfigConfiguration from './SelectXorSelectAnalysisConfigConfiguration.vue'
  import TextXorSelectAnalysisConfigConfiguration from './TextXorSelectAnalysisConfigConfiguration.vue'
  import { AnalysisConfigsUiService } from '../services/AnalysisConfigsUiService'
  import { useWindowSize } from '@/composables/useWindowSize'

  interface IAnalysisConfigConfigurationProps {
    configSchemas: Array<AnalysisConfigSchema>
    analysisConfig: AnalysisConfig | null
    rootAnalysisConfig: AnalysisConfig | null
    uiFieldGroup?: AnalysisConfigSchemaUiFieldGroup
    availableColumns: Array<{ value: string; label: string }>
    configurationLevel: number
    showDescription?: boolean
    isCondensedLayout: boolean
    globalCollapsed?: { collapsed: boolean }
    currentPath?: string[]
  }

  interface IAnalysisConfigConfigurationEmits {
    (
      e: 'configuration-updated',
      config: AnalysisConfig,
      newConfig: Record<string, any>,
      typeChange?: boolean
    ): void
  }

  const props = defineProps<IAnalysisConfigConfigurationProps>()
  const emit = defineEmits<IAnalysisConfigConfigurationEmits>()

  //TODO: add validation should we use forms?
  const formRef = ref()
  const configuration = ref<Record<string, any>>({})
  const isCondensedLayout = ref<boolean>(props.isCondensedLayout)
  const isCollapsed = ref<boolean>(false)
  const globalCollapsed = ref<{ collapsed: boolean }>(props.globalCollapsed ?? { collapsed: false })
  const { controlSize } = useWindowSize()

  watchEffect(() => {
    const newVal = props.globalCollapsed
    if (newVal) {
      globalCollapsed.value = newVal
      isCollapsed.value = !!newVal.collapsed
    }
  })

  const configDescription = ref(props.analysisConfig?.configuration?.description ?? '')

  watch(configDescription, (newVal) => {
    if (configuration.value) {
      configuration.value.description = newVal
      handleConfigurationChange()
    }
  })

  const inputValueChanged$ = new Subject<{ field: string | Array<string>; value: string }>()

  const inputValueChangedSubscription = inputValueChanged$
    .pipe(
      groupBy(({ field }) => field),
      mergeMap((group$) =>
        group$.pipe(
          debounceTime(DEFAULT_INPUT_DEBOUNCE_MS),
          map(({ field, value }) => ({ field, value }))
        )
      )
    )
    .subscribe(({ field, value }) => {
      const parsedValue = typeof value === 'string' ? parseSmartValue(value) : value
      if (Array.isArray(field)) setValueAtPath(field, parsedValue)
      else configuration.value[field] = parsedValue
      handleConfigurationChange()
    })

  // Initialize configuration with default values
  const initializeConfiguration = () => {
    if (!props.analysisConfig) return
    configuration.value = AnalysisConfigService.initializeAnalysisConfigConfiguration(
      props.analysisConfig,
      (props.analysisConfig.schema as any)?.fields ?? []
    )
  }

  const handleConfigurationChange = () => {
    if (props.analysisConfig) {
      emit('configuration-updated', props.analysisConfig, configuration.value)
    }
  }

  const handleChildConfigTypeChanged = (
    configType: string,
    childConfig: AnalysisConfig,
    configObj: any
  ) => {
    if (childConfig) {
      emit('configuration-updated', { ...childConfig, _type: configType } as any, configObj, true)
    }
  }

  const handleConfigTypeChanged = (configType: string, configObj: any) => {
    if (props.analysisConfig) {
      emit(
        'configuration-updated',
        { ...props.analysisConfig, _type: configType } as any,
        configObj?.value,
        true
      )
    }
  }

  watch(
    () => props.analysisConfig,
    (newConfig) => {
      if (newConfig) {
        configuration.value = { ...(newConfig.configuration ?? {}) }
        // initialize with defaults
        if (Object.keys(configuration.value).length === 0) {
          initializeConfiguration()
          // Ensure JSON Export has initial JSON config state
          handleConfigurationChange()
        }
        // ensure any non-schema properties are initialized
        configDescription.value = configuration.value.description ?? ''
      }
    },
    { immediate: true, deep: true }
  )

  const pendingRefocus = inject(pendingRefocusKey)

  let isUnmounting = false

  const onInputChange = (newValue: any, fieldName: string | Array<string>) => {
    const fieldKey = Array.isArray(fieldName) ? fieldName.join('.') : String(fieldName)
    const activeEl = document.activeElement as HTMLInputElement | null
    if (pendingRefocus) {
      pendingRefocus.value = {
        levelFieldKey: `${props.configurationLevel}:${fieldKey}`,
        cursor: activeEl?.selectionStart ?? null
      }
    }
    inputValueChanged$.next({ value: newValue, field: fieldName })
  }

  const onInputBlur = (fieldKey: string) => {
    if (isUnmounting) return
    if (pendingRefocus?.value?.levelFieldKey === `${props.configurationLevel}:${fieldKey}`) {
      pendingRefocus.value = null
    }
  }

  const toggleCollapse = () => {
    isCollapsed.value = !isCollapsed.value
  }

  const toggleGlobalCollapse = () => {
    globalCollapsed.value = { collapsed: !isCollapsed.value }
    isCollapsed.value = !isCollapsed.value
  }

  const onLayoutModeChanged = (e: any) => {
    isCondensedLayout.value = e.target.value
  }

  // use configuredFields (resolved field + path) when available, else schema.fields
  const simpleFieldCount = computed(() => {
    const fieldList: AnalysisConfigField[] =
      Array.isArray(configuredUiFields?.value) && configuredUiFields.value.length
        ? configuredUiFields.value.map((e: any) => e.field)
        : props.analysisConfig
          ? props.analysisConfig.schema.fields
          : []
    return fieldList.filter(
      (f: AnalysisConfigField) =>
        f.field_type !== AnalysisConfigFieldType.Object &&
        f.field_type !== AnalysisConfigFieldType.Array &&
        f.field_type !== AnalysisConfigFieldType.Json
    ).length
  })

  const pathRefs: Record<string, any> = reactive({})
  const valueMappedFieldRefWatchers = new Map<string, (() => void) | undefined>()

  const resolveFieldPath = (allFields: AnalysisConfigField[], segments: string[]) => {
    if (!segments?.length) return null
    let current = allFields.find((f) => f.name === segments[0])
    if (current == null) return null
    const path = [current.name]
    for (let i = 1; i < segments.length; i++) {
      const seg = segments[i]
      if (current && Array.isArray(current.fields)) {
        const child: AnalysisConfigField | undefined = current.fields.find(
          (f: AnalysisConfigField) => f.name === seg
        )
        if (child == null) return null
        current = child
        path.push(seg)
        continue
      }
      if (current && current.item_schema && typeof (current as any).item_schema === 'object') {
        const item: AnalysisConfigField = current.item_schema
        if (Array.isArray(item.fields)) {
          const child = item.fields.find((f: AnalysisConfigField) => f.name === seg)
          if (!child) return null
          current = child
          path.push(seg)
          continue
        }
        return null
      }
      return null
    }
    return { field: current as AnalysisConfigField, path, pathKey: path.join('.') }
  }

  const configuredUiFields = computed(() => {
    const cfg = props.analysisConfig
    const uiGroup = props.uiFieldGroup
    if (!cfg) return []
    const allFields = (cfg.schema as any).fields as AnalysisConfigField[]

    if (!uiGroup || !Array.isArray(uiGroup.fields) || uiGroup.fields.length === 0) {
      return allFields.map((f) => ({ field: f, path: [f.name], pathKey: f.name }))
    }

    const resolved: Array<{ field: AnalysisConfigField; path: string[]; pathKey: string }> = []

    for (const entry of uiGroup.fields) {
      if (typeof entry !== 'string') continue
      const segments = entry
        .split('.')
        .map((s) => s.trim())
        .filter(Boolean)
      const r = resolveFieldPath(allFields, segments)
      if (r) {
        resolved.push(r)
      } else {
        // policy: either warn or throw. Prefer throwing earlier via AnalysisConfigsUiService validation.
        console.warn(`uiFieldGroup entry not found in schema: ${entry}`)
      }
    }
    return resolved
  })

  const levelBasedBackgroundColor = computed(() => {
    // backgroundColorClamp configurationLevel to min 1
    const level = Math.max(props.configurationLevel, 1)
    // Opacity increases by 1% per level, starting from 0% at level 1
    const opacity = Math.min((level - 1) * 0.005, 1)
    return `rgba(0, 0, 0, ${opacity.toFixed(2)})`
  })

  const shouldShowGlobalCollapseButton = computed(() => {
    return (
      (props.analysisConfig?.schema as any)?.fields?.findIndex(
        (x: AnalysisConfigField) =>
          x.field_type === AnalysisConfigFieldType.Array ||
          x.field_type === AnalysisConfigFieldType.Object ||
          x.field_type === AnalysisConfigFieldType.Json
      ) !== -1
    )
  })

  const shouldShowFieldInformation = (field: AnalysisConfigField): boolean => {
    return (
      field.field_type !== AnalysisConfigFieldType.Object &&
      field.field_type !== AnalysisConfigFieldType.Json
    )
  }

  //TODO move this to UIservice? Use in objectbuilder?
  const setValueAtPath = (path: string[], value: any) => {
    if (!path || path.length === 0) return
    let cur: any = configuration.value
    for (let i = 0; i < path.length - 1; i++) {
      const seg = path[i]
      if (cur[seg] == null || typeof cur[seg] !== 'object') cur[seg] = {}
      cur = cur[seg]
    }
    cur[path[path.length - 1]] = value
  }

  // create a computed ref for a nested path that reads/writes into the single canonical configuration object
  const makePathRef = (path: string[]): WritableComputedRef<any> => {
    return computed({
      get: () => {
        return AnalysisConfigsUiService.getValueByPath(configuration.value, path)
      },
      set: (v: any) => {
        setValueAtPath(path, v)
        // reuse existing update flow so parent components stay in sync
        handleConfigurationChange()
      }
    })
  }

  function ensurePathRef(
    path: string[],
    pathKey: string,
    field: AnalysisConfigField,
    defaultValue?: any
  ) {
    if (!pathKey) pathKey = path.join('.')
    if (!pathRefs[pathKey]) {
      let pathRef = null
      pathRef = makePathRef(path)

      const existingValue = AnalysisConfigsUiService.getValueByPath(configuration.value, path)
      const defaultValueWasPassed = arguments.length >= 4
      if (existingValue === undefined) {
        if (defaultValueWasPassed) pathRef.value = defaultValue
        else if (field.default_value != undefined) pathRef.value = field.default_value
      }

      pathRefs[pathKey] = pathRef
    }
    return pathRefs[pathKey]
  }

  // Used to Value Mapped fields to ensure values are synchronized
  const setupValueMappedWatcher = (
    srcSegments: string[],
    targetPath: string[],
    field: AnalysisConfigField,
    preserveExisting?: boolean,
    srcValueConditions?: Array<{ value_prop_path: string; src_prop_value: any }>
  ) => {
    const srcKey = srcSegments.join('.')
    const watchKey = `${srcKey}=>${targetPath.join('.')}`

    if (valueMappedFieldRefWatchers.has(watchKey)) return

    // ensure a reactive ref exists for the source path (raw)
    if (!pathRefs[srcKey]) {
      pathRefs[srcKey] = makePathRef(srcSegments)
    }

    // safe getter that prefers the reactive pathRef when available,
    // otherwise falls back to reading the canonical configuration object.
    const getSrcValue = () => {
      let value: any = undefined

      const maybeRef = pathRefs[srcKey]
      if (maybeRef && typeof maybeRef === 'object' && 'value' in maybeRef) {
        value = maybeRef.value
      }
      // fallback: read directly from configuration (defensive)
      value = AnalysisConfigsUiService.getValueByPath(configuration.value, srcSegments)

      if (Array.isArray(srcValueConditions)) {
        const match = srcValueConditions.find((x) => x.src_prop_value === value)
        if (match)
          value = AnalysisConfigsUiService.getValueByPath(
            configuration.value,
            match.value_prop_path
          )
      }

      return value
    }

    // initial sync: transform source value into target form and write to target path
    try {
      const oldValue = preserveExisting
        ? AnalysisConfigsUiService.getValueByPath(configuration.value, targetPath)
        : undefined
      const transformed = AnalysisConfigsUiService.transformValueForValueMappedField(
        field,
        getSrcValue(),
        oldValue
      )
      setValueAtPath(targetPath, transformed)
      handleConfigurationChange()
    } catch (err) {
      console.warn('Failed initial sync for mapped field', watchKey, err)
    }

    // watch source via safe getter and copy changes to target
    const stop = watch(getSrcValue, (newVal) => {
      try {
        const oldValue = preserveExisting
          ? AnalysisConfigsUiService.getValueByPath(configuration.value, targetPath)
          : undefined
        const transformed = AnalysisConfigsUiService.transformValueForValueMappedField(
          field,
          newVal,
          oldValue
        )
        setValueAtPath(targetPath, transformed)
        handleConfigurationChange()
      } catch (err) {
        console.warn('Failed mapped-field watch handler', watchKey, err)
      }
    })

    valueMappedFieldRefWatchers.set(watchKey, stop)
  }

  // When configuredUiFields changes, ensure refs exist
  watch(
    () => configuredUiFields.value.map((f: any) => f.pathKey),
    (keys) => {
      if (!Array.isArray(keys)) return
      keys.forEach((k: string) => {
        const entry = configuredUiFields.value.find((e: any) => e.pathKey === k)
        if (entry) {
          ensurePathRef(entry.path, entry.pathKey, entry.field)

          // field_type_args mappings
          // Value Mapped handled in ensurePathRef
          if (AnalysisConfigsUiService.isValueMappedField(entry.field)) {
            const srcPropPath = entry.field.field_type_args?.value_map?.src_prop_path
            const preserveExisting = !!entry.field.field_type_args?.value_map?.preserve_existing
            const srcSegments = srcPropPath
              .split('.')
              .map((s: string) => s.trim())
              .filter(Boolean)
            const srcValueConditions = entry.field.field_type_args?.value_map?.src_value_conditions
            // create a raw ref and setup watcher to sync source->target
            setupValueMappedWatcher(
              srcSegments,
              entry.path,
              entry.field,
              preserveExisting,
              srcValueConditions
            )
          }

          // XOR - ensure a pathRef exists for an alternate field name (used by SelectXorText)
          const altName = entry.field.field_type_args?.xor_alt_field_name
          if (typeof altName === 'string' && altName.length > 0) {
            const defaultValue = entry.field.field_type_args?.xor_inactive_field_default_value
            const altPath = replaceLast(entry.path, altName)
            const altKey = altPath.join('.')
            if (!pathRefs[altKey]) {
              // pass the same field object — ensurePathRef doesn't rely strictly on this being
              // the exact schema field for the alternate path (it only uses default_value if present)
              ensurePathRef(altPath, altKey, entry.field, defaultValue)
            }
          }

          //end field_type_args mappings
        }
      })
    },
    { immediate: true }
  )

  const checkboxSizeClass = computed(() => {
    if (controlSize.value === 'small') return 'checkbox-sm'
    if (controlSize.value === 'large') return 'checkbox-lg'
    return 'checkbox-md'
  })

  const displayField = computed(() => {
    const mappedField = props.analysisConfig?.display_field as
      | string
      | Array<string>
      | null
      | undefined
    if (mappedField == null) return undefined

    const fields = props.analysisConfig?.schema?.fields ?? []

    const valueForName = (name: string) => {
      const matchingField = fields.find((x: any) => x.name === name)
      if (!matchingField) return undefined
      const val = configuration.value?.[matchingField.name]
      return val === undefined ? undefined : val
    }

    if (typeof mappedField === 'string') {
      return valueForName(mappedField)
    }

    if (Array.isArray(mappedField)) {
      for (const name of mappedField) {
        const v = valueForName(name)
        if (v) return v
      }
    }

    return undefined
  })

  onMounted(() => {
    if (pendingRefocus?.value) {
      const { levelFieldKey, cursor } = pendingRefocus.value
      let el = document.querySelector(`[data-field="${levelFieldKey}"]`) as HTMLElement | null
      if (el && el.tagName !== 'INPUT') el = el.querySelector('input') ?? el
      const inputEl = el as HTMLInputElement | null
      if (inputEl?.tagName === 'INPUT') {
        inputEl.focus()
        if (cursor !== null) {
          try { inputEl.setSelectionRange(cursor, cursor) } catch {}
        }
      }
      pendingRefocus.value = null
    }
  })

  onBeforeUnmount(() => {
    isUnmounting = true
    inputValueChangedSubscription?.unsubscribe()
    valueMappedFieldRefWatchers.forEach((stop) => {
      if (typeof stop === 'function') stop()
    })
    valueMappedFieldRefWatchers.clear()
  })
</script>

<template>
  <div
    v-if="analysisConfig"
    :style="{ backgroundColor: levelBasedBackgroundColor }"
    :class="[
      'border',
      'analysis-config-configuration',
      'h-full',
      'w-full',
      {
        '!bg-[var(--card)] dark:!bg-[var(--card-light)]': props.configurationLevel % 2 !== 0,
        '!bg-[var(--card-light)] dark:!bg-[var(--card)]': props.configurationLevel % 2 === 0,
        'child-config-configuration': props.configurationLevel > 1
      }
    ]"
  >
    <div class="flex h-full flex-col">
      <!-- Only show radio group if configurationLevel is 1 or less -->
      <div v-if="props.configurationLevel <= 1" class="mb-2 flex items-center justify-end gap-2">
        <a-tooltip>
          <template #title>Toggle layout mode</template>
          <a-radio-group
            :value="isCondensedLayout"
            @change="onLayoutModeChanged"
            :size="controlSize"
          >
            <a-radio-button :value="true">
              <AppstoreOutlined class="radio-icon-center" />
            </a-radio-button>
            <a-radio-button :value="false">
              <BorderOutlined class="radio-icon-center" />
            </a-radio-button>
          </a-radio-group>
        </a-tooltip>
      </div>
      <div class="horizontal-rule mb-4 border-b pb-1" v-if="props.configurationLevel > 1">
        <div class="flex">
          <div>
            <h3 class="mb-1 text-lg font-semibold">Configure: {{ analysisConfig.display_name }}</h3>
            <span class="ml-1" v-if="displayField != null">{{ `(${displayField})` }}</span>
          </div>
          <div class="ml-auto flex">
            <a-tooltip placement="top">
              <template #title>Toggle Collapse</template>
              <Button
                style="padding: 3px; border: none"
                type="text"
                :size="controlSize"
                class="flex items-center justify-center"
                @click="toggleCollapse"
                aria-label="Toggle Collapse"
              >
                <PlusSquare
                  v-if="isCollapsed"
                  class="text-base"
                  style="line-height: 1; margin: 0"
                />
                <MinusSquare v-else class="text-base" style="line-height: 1; margin: 0" />
              </Button>
            </a-tooltip>

            <a-tooltip v-if="shouldShowGlobalCollapseButton" placement="top">
              <template #title>{{ isCollapsed ? 'Expand All' : 'Collapse All' }}</template>
              <Button
                v-if="shouldShowGlobalCollapseButton"
                style="padding: 3px; border: none"
                type="text"
                :size="controlSize"
                class="flex items-center justify-center"
                @click="toggleGlobalCollapse"
                aria-label="Toggle Collapse All"
              >
                <ExpandOutlined
                  v-if="isCollapsed"
                  class="rotate-90 rounded-sm border-gray-300 p-1 text-base"
                  style="line-height: 1; margin: 0"
                />
                <CompressOutlined
                  v-else
                  class="rotate-90 rounded-sm border-gray-300 p-1 text-base"
                  style="line-height: 1; margin: 0"
                />
              </Button>
            </a-tooltip>
          </div>
        </div>
      </div>
      <div
        v-else-if="
          analysisConfig._type !== AnalysisConfigService.CUSTOM_ANALYSISCONFIG_TYPE &&
          props.showDescription === true
        "
        class="mb-4"
      >
        <label class="mb-1 block text-sm font-medium">Name</label>
        <Input
          v-model:value="configDescription"
          @change="handleConfigurationChange"
          placeholder="Configuration Name"
          :size="controlSize"
        />
      </div>

      <!-- Configuration Form -->
      <div
        :class="['flex-1', 'overflow-y-auto', { 'collapsed-config-configuration': isCollapsed }]"
      >
        <Form ref="formRef" :model="configuration" layout="vertical">
          <div
            :class="{
              'config-form-fields-grid': true,
              expanded:
                props.configurationLevel === 1 ? !isCondensedLayout : !props.isCondensedLayout
            }"
          >
            <div
              v-for="configuredField in configuredUiFields"
              :key="configuredField.field.name"
              class="config-form-field-grid"
              :class="{
                'array-config-builder-grid-span':
                  configuredField.field.field_type === AnalysisConfigFieldType.Array,
                'object-config-builder-grid-span':
                  configuredField.field.field_type === AnalysisConfigFieldType.Object ||
                  configuredField.field.field_type === AnalysisConfigFieldType.StringTemplate,
                'json-config-configuration-grid-span':
                  configuredField.field.field_type === AnalysisConfigFieldType.Json,
                'single-field-grid-span':
                  simpleFieldCount <= 1 &&
                  configuredField.field.field_type !== AnalysisConfigFieldType.Object &&
                  configuredField.field.field_type !== AnalysisConfigFieldType.Array &&
                  configuredField.field.field_type !== AnalysisConfigFieldType.Json,
                '!hidden': !AnalysisConfigsUiService.isFieldVisibleFromRoot(
                  configuredField.field,
                  props.rootAnalysisConfig,
                  props.currentPath
                    ? [...props.currentPath, ...configuredField.path]
                    : configuredField.path
                )
              }"
            >
              <div class="flex">
                <div
                  v-if="shouldShowFieldInformation(configuredField.field)"
                  class="min-w-0 flex-1"
                >
                  <label class="table-field-label mb-1 block text-sm font-medium">
                    {{ configuredField.field.display_name }}
                    <span v-if="configuredField.field.required" class="text-red-500">*</span>
                  </label>
                  <p
                    v-if="configuredField.field.description"
                    class="table-field-label mb-2 text-xs"
                  >
                    {{ configuredField.field.description }}
                  </p>
                </div>

                <div
                  v-if="
                    configuredField.field.help_content != null &&
                    configuredField.field.help_content?.length > 0
                  "
                  class="m1-auto flex justify-end"
                >
                  <AnalysisConfigHelpPopover
                    :content-type="configuredField.field.help_content"
                    :title="`${configuredField.field.display_name} Help`"
                  />
                </div>
              </div>

              <!-- Text Input -->
              <Input
                v-if="configuredField.field.field_type === AnalysisConfigFieldType.Text"
                :value="pathRefs[configuredField.pathKey]"
                @update:value="(val: string) => onInputChange(val, configuredField.path)"
                @blur="() => onInputBlur(configuredField.pathKey)"
                :data-field="`${props.configurationLevel}:${configuredField.pathKey}`"
                :placeholder="configuredField.field.placeholder || ''"
                :readonly="
                  configuredField.field.readonly ||
                  !!configuredField.field.field_type_args?.value_map?.src_prop_path
                "
                :disabled="
                  configuredField.field.readonly ||
                  !!configuredField.field.field_type_args?.value_map?.src_prop_path
                "
                :size="controlSize"
              />

              <!-- Number Input -->
              <InputNumber
                v-else-if="configuredField.field.field_type === AnalysisConfigFieldType.Number"
                :value="pathRefs[configuredField.pathKey]"
                @update:value="
                  (val: number) => {
                    setValueAtPath(configuredField.path, val)
                    handleConfigurationChange()
                  }
                "
                :placeholder="configuredField.field.placeholder || ''"
                :size="controlSize"
                :disabled="configuredField.field.readonly"
                :readonly="configuredField.field.readonly"
                class="w-full"
              />

              <RegexAnalysisConfigConfiguration
                v-else-if="
                  configuredField.field.field_type === AnalysisConfigFieldType.RegularExpression
                "
                v-model="pathRefs[configuredField.pathKey]"
                :required="configuredField.field.required"
                :regex="configuredField.field.field_type_args?.regex_pattern"
                :data-type="configuredField.field.field_type_args?.regex_data_type"
                :placeholder="configuredField.field.placeholder || ''"
                :error-message="configuredField.field.field_type_args?.regex_description"
                @update:modelValue="handleConfigurationChange"
              />

              <!-- Select Dropdown -->
              <Select
                v-else-if="configuredField.field.field_type === AnalysisConfigFieldType.Select"
                v-model:value="pathRefs[configuredField.pathKey]"
                :placeholder="`Select ${configuredField.field.display_name.toLowerCase()}`"
                @change="handleConfigurationChange"
                :size="controlSize"
                :disabled="configuredField.field.readonly"
                :readonly="configuredField.field.readonly"
                class="w-full"
                showSearch
                allowClear
              >
                <SelectOption
                  v-for="option in configuredField.field.options"
                  :key="option.value"
                  :value="option.value"
                >
                  <span v-html="option.label"></span>
                </SelectOption>
              </Select>

              <!-- Select Dropdown JSON Payload -->
              <Select
                v-else-if="configuredField.field.field_type === AnalysisConfigFieldType.SelectJson"
                :value="JSON.stringify(pathRefs[configuredField.pathKey])"
                :placeholder="`Select ${configuredField.field.display_name.toLowerCase()}`"
                @update:value="
                  (val: string) => {
                    try {
                      const parsed = JSON.parse(val)
                      setValueAtPath(configuredField.path, parsed)
                      handleConfigurationChange()
                    } catch (e) {
                      console.error('Failed to parse selected JSON value', e)
                    }
                  }
                "
                :size="controlSize"
                :disabled="configuredField.field.readonly"
                :readonly="configuredField.field.readonly"
                class="w-full"
                showSearch
                allowClear
              >
                <SelectOption
                  v-for="option in configuredField.field.options"
                  :key="JSON.stringify(option.value)"
                  :value="JSON.stringify(option.value)"
                >
                  <span v-html="option.label"></span>
                </SelectOption>
              </Select>

              <!-- Select Dropdown Or Create -->
              <SelectOrCreateAnalysisConfigConfiguration
                v-else-if="
                  configuredField.field.field_type === AnalysisConfigFieldType.SelectOrCreate
                "
                v-model="pathRefs[configuredField.pathKey]"
                :placeholder="`Select or enter new ${configuredField.field.display_name.toLowerCase()}`"
                :options="configuredField.field.options as AnalysisConfigOption[]"
                :disabled="configuredField.field.readonly"
                :readonly="configuredField.field.readonly"
                @update:modelValue="handleConfigurationChange"
              />

              <!-- Multi-Select -->
              <Select
                v-else-if="
                  configuredField.field.field_type === AnalysisConfigFieldType.MultiSelect ||
                  configuredField.field.field_type === AnalysisConfigFieldType.ColumnMultiSelect
                "
                v-model:value="pathRefs[configuredField.pathKey]"
                mode="multiple"
                :placeholder="`Select ${configuredField.field.display_name.toLowerCase()}`"
                @change="handleConfigurationChange"
                :size="controlSize"
                :disabled="configuredField.field.readonly"
                :readonly="configuredField.field.readonly"
                class="w-full"
                showSearch
                allowClear
              >
                <SelectOption
                  v-if="configuredField.field.field_type === AnalysisConfigFieldType.MultiSelect"
                  v-for="option in configuredField.field.options"
                  :key="option.value"
                  :value="option.value"
                >
                  <span v-html="option.label"></span>
                </SelectOption>

                <SelectOption
                  v-if="
                    configuredField.field.field_type === AnalysisConfigFieldType.ColumnMultiSelect
                  "
                  v-for="option in props.availableColumns"
                  :key="option.value"
                  :value="option.value"
                >
                  <span v-html="option.label"></span>
                </SelectOption>
              </Select>

              <!-- Boolean Checkbox -->
              <Checkbox
                v-else-if="configuredField.field.field_type === AnalysisConfigFieldType.Boolean"
                @change="handleConfigurationChange"
                v-model:checked="pathRefs[configuredField.pathKey]"
                :disabled="configuredField.field.readonly"
                :readonly="configuredField.field.readonly"
                :class="checkboxSizeClass"
              >
                {{ configuredField.field.display_name }}
              </Checkbox>

              <!-- Column Select -->
              <Select
                v-else-if="
                  configuredField.field.field_type === AnalysisConfigFieldType.ColumnSelect
                "
                v-model:value="pathRefs[configuredField.pathKey]"
                :placeholder="`Select ${configuredField.field.display_name.toLowerCase()}`"
                @change="handleConfigurationChange"
                :disabled="configuredField.field.readonly"
                :readonly="configuredField.field.readonly"
                :size="controlSize"
                class="w-full"
                showSearch
                allowClear
              >
                <SelectOption
                  v-for="column in props.availableColumns"
                  :key="column.value"
                  :value="column.value"
                >
                  <span v-html="column.value"></span>
                </SelectOption>
              </Select>

              <!-- Column Select or Create -->
              <AutoComplete
                v-else-if="
                  configuredField.field.field_type === AnalysisConfigFieldType.ColumnSelectOrCreate
                "
                v-model:value="pathRefs[configuredField.pathKey]"
                :options="props.availableColumns"
                :placeholder="
                  configuredField.field.placeholder ||
                  `Select existing column or enter new column name`
                "
                :size="controlSize"
                class="w-full"
                @change="handleConfigurationChange"
                showSearch
                allowClear
              />

              <!-- Multi Column Select Or Create -->
              <ColumnMultiSelectOrCreateAnalysisConfigConfiguration
                v-else-if="
                  configuredField.field.field_type ===
                  AnalysisConfigFieldType.ColumnMultiSelectOrCreate
                "
                v-model="pathRefs[configuredField.pathKey]"
                :placeholder="`Select or enter new ${configuredField.field.display_name.toLowerCase()} values`"
                :available-columns="props.availableColumns"
                @update:modelValue="handleConfigurationChange"
              />

              <!-- Array Values -->
              <ArrayValuesAnalysisConfigConfiguration
                v-else-if="configuredField.field.field_type === AnalysisConfigFieldType.ArrayValues"
                v-model="pathRefs[configuredField.pathKey]"
                :place-holder="configuredField.field.placeholder || 'Enter value(s)'"
                @update:modelValue="handleConfigurationChange"
              />

              <!-- Color Picker -->
              <ColorAnalysisConfigConfiguration
                v-else-if="configuredField.field.field_type === AnalysisConfigFieldType.ColorPicker"
                v-model="configuration[configuredField.field.name]"
                :place-holder="configuredField.field.placeholder || 'Select color'"
                @update:modelValue="handleConfigurationChange"
              />

              <!-- Array Type -->
              <ArrayAnalysisConfigBuilder
                v-else-if="configuredField.field.field_type === AnalysisConfigFieldType.Array"
                :root-analysis-config="props.rootAnalysisConfig"
                :configuration-level="props.configurationLevel"
                :available-columns="props.availableColumns"
                v-model="pathRefs[configuredField.pathKey]"
                :analysis-config-field="configuredField.field"
                :key="analysisConfig?.id"
                @child-config-type-changed="handleChildConfigTypeChanged"
                :config-schemas="configSchemas"
                :is-condensed-layout="
                  props.configurationLevel === 1 ? isCondensedLayout : props.isCondensedLayout
                "
                :global-collapsed="globalCollapsed"
                :current-path="
                  props.currentPath
                    ? [...props.currentPath, ...configuredField.path]
                    : configuredField.path
                "
              />

              <!-- Object Type -->
              <ObjectAnalysisConfigBuilder
                v-else-if="configuredField.field.field_type === AnalysisConfigFieldType.Object"
                :root-analysis-config="props.rootAnalysisConfig"
                :configuration-level="props.configurationLevel + 1"
                :available-columns="props.availableColumns"
                v-model="pathRefs[configuredField.pathKey]"
                :analysis-config-field="configuredField.field"
                @update:modelValue="handleConfigurationChange"
                :config-schemas="configSchemas"
                :is-condensed-layout="
                  props.configurationLevel === 1 ? isCondensedLayout : props.isCondensedLayout
                "
                :global-collapsed="globalCollapsed"
                :current-path="
                  props.currentPath
                    ? [...props.currentPath, ...configuredField.path]
                    : configuredField.path
                "
              />

              <!-- String Template -->
              <StringTemplateBuilder
                v-else-if="
                  configuredField.field.field_type === AnalysisConfigFieldType.StringTemplate
                "
                :configuration-level="props.configurationLevel + 1"
                :available-columns="props.availableColumns"
                :delimiter_start="configuredField.field.field_type_args?.delimiter_start"
                :delimiter_end="configuredField.field.field_type_args?.delimiter_end"
                v-model="pathRefs[configuredField.pathKey]"
                :place-holder="configuredField.field.placeholder"
                @update:modelValue="handleConfigurationChange"
                :is-condensed-layout="
                  props.configurationLevel === 1 ? isCondensedLayout : props.isCondensedLayout
                "
                :global-collapsed="globalCollapsed"
              />

              <!-- Select XOR Text -->
              <SelectXorTextAnalysisConfigConfiguration
                v-else-if="
                  configuredField.field.field_type === AnalysisConfigFieldType.SelectXorText
                "
                v-model="pathRefs[configuredField.pathKey]"
                :default-model-value="configuredField.field.default_value"
                @update:modelValue="handleConfigurationChange"
                v-model:alternativeModelValue="
                  pathRefs[
                    replaceLast(
                      configuredField.path,
                      configuredField.field.field_type_args?.xor_alt_field_name
                    ).join('.')
                  ]
                "
                @update:alternativeModelValue="handleConfigurationChange"
                :inactive-field-default-value="
                  configuredField.field.field_type_args?.xor_inactive_field_default_value
                "
                :toggle-label="configuredField.field.field_type_args?.xor_toggle_label"
                :options="
                  configuredField.field.field_type_args?.xor_use_column_options === true
                    ? props.availableColumns
                    : (configuredField.field.options as AnalysisConfigOption[])
                "
                :place-holder="configuredField.field.placeholder"
              />

              <!-- Select XOR Select -->
              <SelectXorSelectAnalysisConfigConfiguration
                v-else-if="
                  configuredField.field.field_type === AnalysisConfigFieldType.SelectXorSelect
                "
                v-model="pathRefs[configuredField.pathKey]"
                :default-model-value="configuredField.field.default_value"
                @update:modelValue="handleConfigurationChange"
                v-model:alternativeModelValue="
                  pathRefs[
                    replaceLast(
                      configuredField.path,
                      configuredField.field.field_type_args?.xor_alt_field_name
                    ).join('.')
                  ]
                "
                @update:alternativeModelValue="handleConfigurationChange"
                :inactive-field-default-value="
                  configuredField.field.field_type_args?.xor_inactive_field_default_value
                "
                :toggle-label="configuredField.field.field_type_args?.xor_toggle_label"
                :options="
                  configuredField.field.field_type_args?.xor_use_column_options === true
                    ? props.availableColumns
                    : (configuredField.field.options as AnalysisConfigOption[])
                "
                :alternative-options="
                  configuredField.field.field_type_args?.xor_alt_field_use_column_options === true
                    ? props.availableColumns
                    : (configuredField.field.field_type_args
                        ?.xor_alt_field_options as AnalysisConfigOption[])
                "
                :place-holder="configuredField.field.placeholder"
              />

              <!-- Text XOR Select -->
              <TextXorSelectAnalysisConfigConfiguration
                v-else-if="
                  configuredField.field.field_type === AnalysisConfigFieldType.TextXorSelect
                "
                v-model="pathRefs[configuredField.pathKey]"
                :default-model-value="configuredField.field.default_value"
                @update:modelValue="handleConfigurationChange"
                v-model:alternativeModelValue="
                  pathRefs[
                    replaceLast(
                      configuredField.path,
                      configuredField.field.field_type_args?.xor_alt_field_name
                    ).join('.')
                  ]
                "
                @update:alternativeModelValue="handleConfigurationChange"
                :inactive-field-default-value="
                  configuredField.field.field_type_args?.xor_inactive_field_default_value
                "
                :toggle-label="configuredField.field.field_type_args?.xor_toggle_label"
                :alternative-options="
                  configuredField.field.field_type_args?.xor_alt_field_use_column_options === true
                    ? props.availableColumns
                    : (configuredField.field.field_type_args
                        ?.xor_alt_field_options as AnalysisConfigOption[])
                "
                :place-holder="configuredField.field.placeholder"
              />

              <!-- Json Type (Custom Config) -->
              <JsonAnalysisConfigConfiguration
                v-else-if="configuredField.field.field_type === AnalysisConfigFieldType.Json"
                :analysis-config-schemas="props.configSchemas"
                :available-columns="props.availableColumns"
                v-model="configuration"
                :editor-schema="
                  AnalysisConfigJsonConfigurationService.getJsonEditorSchemaForAnalysisConfig()
                "
                :place-holder="configuredField.field.placeholder || 'Configure Json'"
                :configuration-level="props.configurationLevel"
                @analysis-config-type-changed="handleConfigTypeChanged"
                @update:modelValue="handleConfigurationChange"
                :allow-type-change="true"
              />

              <!-- Default fallback for unknown field types -->
              <JsonAnalysisConfigConfiguration
                v-else
                :analysis-config-schemas="props.configSchemas"
                :available-columns="props.availableColumns"
                v-model="pathRefs[configuredField.pathKey]"
                :editor-schema="
                  AnalysisConfigJsonConfigurationService.getJsonEditorSchemaForField(
                    configuredField.field
                  )
                "
                :place-holder="configuredField.field.placeholder || 'Configure Json'"
                :configuration-level="props.configurationLevel"
                @update:modelValue="handleConfigurationChange"
                :allow-type-change="false"
              />
            </div>
          </div>
        </Form>
      </div>
    </div>
  </div>

  <!-- Empty State -->
  <div v-else class="flex h-full items-center justify-center text-gray-500">
    <div class="mt-8 text-center">
      <p class="mb-2 text-lg">No Configuration Selected</p>
      <p class="text-sm">Select a configuration from the left panel to configure its settings</p>
    </div>
  </div>
</template>

<style scoped>
  .analysis-config-configuration {
    padding: 10px;
  }

  .collapsed-config-configuration {
    display: none;
  }

  .horizontal-rule {
    border-bottom-color: #0000001f;
  }

  /* Custom scrollbar for form area */
  .overflow-y-auto::-webkit-scrollbar {
    width: 6px;
  }

  .overflow-y-auto::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }

  .overflow-y-auto::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
  }

  .overflow-y-auto::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }

  .config-form-fields-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 16px;
  }

  @media (min-width: 1100px) {
    .config-form-fields-grid:not(.expanded) {
      grid-template-columns: repeat(2, 1fr);
    }
  }

  .config-form-field-grid {
    width: 100%;
    min-height: 90px;
    max-height: 130px;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    overflow: hidden;
  }

  .array-config-builder-grid-span,
  .object-config-builder-grid-span,
  .json-config-configuration-grid-span,
  .single-field-grid-span {
    grid-column: 1 / -1 !important;
    min-height: unset;
    max-height: unset;
    overflow: visible;
  }

  .plot-field-label,
  .table-field-label {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
    display: block;
  }

  .plot-field-description {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
    display: block;
  }

  .radio-icon-center {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    font-size: 18px;
  }

  /* Ensure checkbox size classes are available (RuleConfiguration already added these; keep consistent) */
  .checkbox-sm {
    font-size: 12px;
  }
  .checkbox-sm .ant-checkbox-inner {
    width: 14px;
    height: 14px;
  }
  .checkbox-md {
    font-size: 14px;
  }
  .checkbox-md .ant-checkbox-inner {
    width: 16px;
    height: 16px;
  }
  .checkbox-lg {
    font-size: 16px;
  }
  .checkbox-lg .ant-checkbox-inner {
    width: 20px;
    height: 20px;
  }
</style>
